import React, { useEffect, useMemo, useState } from 'react'
import { Plus, X } from 'lucide-react'
import { adminVendorCategoriesService, adminVendorsService } from '../services'
import ProfileAvatar from '../components/ProfileAvatar'
import './VendorCategories.css'

const VendorCategories = () => {
  const [categories, setCategories] = useState([])
  const [vendors, setVendors] = useState([])
  const [newCategoryName, setNewCategoryName] = useState('')
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState(null)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      try {
        const [categoriesList, vendorsList] = await Promise.all([
          adminVendorCategoriesService.list(),
          adminVendorsService.list(),
        ])
        if (cancelled) return

        setCategories(categoriesList || [])
        setVendors(vendorsList || [])
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.error || e.message || 'Failed to load vendor categories')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [])

  const normalizedVendorCategories = (vendor) => {
    const raw =
      vendor.activityCategories ??
      vendor.activity_categories ??
      vendor.categories ??
      vendor.category ??
      vendor.type ??
      null

    if (Array.isArray(raw)) {
      return raw
        .map((value) => String(value || '').trim())
        .filter(Boolean)
        .map((value) => value.toLowerCase())
    }

    if (typeof raw === 'string' && raw.trim()) {
      return [raw.trim().toLowerCase()]
    }

    return []
  }

  const categoriesWithVendors = useMemo(() => {
    return (categories || []).map((category) => {
      const categoryName = String(category?.name || '').trim().toLowerCase()
      const categoryVendors = (vendors || []).filter((vendor) => {
        return normalizedVendorCategories(vendor).includes(categoryName)
      })

      return {
        ...category,
        vendors: categoryVendors,
      }
    })
  }, [categories, vendors])

  const handleAddCategory = async () => {
    const name = newCategoryName.trim()
    if (!name || adding) return

    try {
      setAdding(true)
      setError(null)
      await adminVendorCategoriesService.create({ name })
      const refreshed = await adminVendorCategoriesService.list()
      setCategories(refreshed || [])
      setNewCategoryName('')
      setShowAddModal(false)
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to create category')
    } finally {
      setAdding(false)
    }
  }

  return (
    <div className="vendor-categories-page">
      <div className="page-header">
        <div className="header-titles">
          <h1>Partners Categories</h1>
          <p>Manage partners categories and review which partners belong to each one.</p>
        </div>
      </div>

      <div className="vendor-categories-actions">
        <button
          type="button"
          className="btn-primary"
          onClick={() => {
            setError(null)
            setNewCategoryName('')
            setShowAddModal(true)
          }}
        >
          <Plus size={16} />
          Add Category
        </button>
      </div>

      {error && <p className="vendor-categories-error">{error}</p>}

      {loading ? (
        <p className="vendor-categories-loading">Loading partners categories...</p>
      ) : categoriesWithVendors.length === 0 ? (
        <div className="vendor-categories-empty">
          <h3>No categories yet</h3>
          <p>Add your first partner category using the form above.</p>
        </div>
      ) : (
        <div className="vendor-categories-grid">
          {categoriesWithVendors.map((category) => (
            <section
              key={category.id}
              className="vendor-category-card"
              onClick={() => setSelectedCategory(category)}
            >
              <div className="vendor-category-card-header">
                <h3>{category.name}</h3>
                <span>{category.vendors.length} partners</span>
              </div>

              <p className="vendor-category-empty">
                Click to view partners in this category.
              </p>
            </section>
          ))}
        </div>
      )}

      {selectedCategory && (
        <div className="vendor-category-modal-overlay" onClick={() => setSelectedCategory(null)}>
          <div className="vendor-category-modal" onClick={(event) => event.stopPropagation()}>
            <div className="vendor-category-modal-header">
              <div>
                <h2>{selectedCategory.name}</h2>
                <p>{selectedCategory.vendors.length} partners</p>
              </div>
              <button
                type="button"
                className="vendor-category-modal-close"
                onClick={() => setSelectedCategory(null)}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {selectedCategory.vendors.length === 0 ? (
              <p className="vendor-category-modal-empty">No partners in this category yet.</p>
            ) : (
              <ul className="vendor-category-vendors">
                {selectedCategory.vendors.map((vendor) => (
                  <li key={vendor.id} className="vendor-category-vendor-item">
                    <div className="vendor-category-vendor-avatar">
                      <ProfileAvatar
                        src={vendor.logoUrl || vendor.logo || vendor.profilePhotoUrl}
                        alt={vendor.name || 'Partner'}
                        className="vendor-category-vendor-avatar-img"
                      />
                    </div>
                    <div className="vendor-category-vendor-details">
                      <p className="vendor-category-vendor-name">{vendor.name || vendor.businessName || 'Partner'}</p>
                      <p className="vendor-category-vendor-type">{vendor.type || vendor.vendorType || 'Partner'}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="vendor-category-modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="vendor-category-modal vendor-category-create-modal" onClick={(event) => event.stopPropagation()}>
            <div className="vendor-category-modal-header">
              <div>
                <h2>Add Category</h2>
                <p>Enter category name</p>
              </div>
              <button
                type="button"
                className="vendor-category-modal-close"
                onClick={() => setShowAddModal(false)}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <input
              type="text"
              className="vendor-category-input"
              placeholder="Category name"
              value={newCategoryName}
              onChange={(event) => setNewCategoryName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  handleAddCategory()
                }
              }}
              autoFocus
              disabled={adding}
            />

            <button
              type="button"
              className="btn-primary vendor-category-create-btn"
              disabled={adding || !newCategoryName.trim()}
              onClick={handleAddCategory}
            >
              {adding ? 'Creating...' : 'Create'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default VendorCategories
